import styled from '@emotion/styled/macro';
import {
  Button,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';

type PipelineCardInfoItemProps = {
  icon?: SvgNames;
  value: string;
  copyAriaLabel?: string;
  onCopy?: (value: string) => void;
  label?: string;
  valueHref?: string;
  onClick?: () => void;
  showCopyButton?: boolean;
  valueColor?: string;
};

const PipelineCardInfoItem = ({
  icon,
  value,
  copyAriaLabel,
  onCopy,
  label,
  valueHref,
  onClick,
  showCopyButton = false,
  valueColor = tokens.BLACK,
}: PipelineCardInfoItemProps) => {
  let valueNode;
  if (valueHref) {
    valueNode = (
      <ProspectInfoValue href={valueHref} $valueColor={valueColor}>
        {value}
      </ProspectInfoValue>
    );
  } else if (onClick) {
    valueNode = (
      <ProspectInfoValueButton
        color="link"
        onClick={onClick}
        $valueColor={valueColor}>
        {value}
      </ProspectInfoValueButton>
    );
  } else {
    valueNode = (
      <ProspectInfoPlainValue size={14} color={valueColor}>
        {value}
      </ProspectInfoPlainValue>
    );
  }

  return (
    <PipelineCardInfoItemContainer>
      <ProspectInfoLeft>
        <IconSlot>
          {icon && (
            <SvgIcon
              name={icon}
              width={12}
              height={12}
              color={tokens.TINTS_OFF_WHITE_20}
            />
          )}
        </IconSlot>
        <ProspectInfoText $valueColor={valueColor}>
          {label && <Typography size={14}>{label}</Typography>}
          {valueNode}
        </ProspectInfoText>
      </ProspectInfoLeft>
      {showCopyButton && onCopy && (
        <CopyFieldButton
          color="tertiary"
          size="sm"
          aria-label={copyAriaLabel}
          onClick={() => onCopy(value)}
          iconMiddle={
            <SvgIcon
              name={SvgNames.Copy}
              width={14}
              height={14}
              color={tokens.BRAND_HERO_BLUE}
            />
          }
        />
      )}
    </PipelineCardInfoItemContainer>
  );
};

const PipelineCardInfoItemContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  box-sizing: border-box;

  &:last-child {
    border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  }
`;

const ProspectInfoLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const IconSlot = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ProspectInfoText = styled.div<{ $valueColor: string }>`
  display: flex;
  flex-direction: row;
  gap: 2px;
  min-width: 0;

  > button {
    border-bottom: 1px solid ${({ $valueColor }) => $valueColor};
  }
`;

const ProspectInfoValue = styled.a<{ $valueColor: string }>`
  font-size: 14px;
  line-height: 140%;
  color: ${({ $valueColor }) => $valueColor};
  text-decoration: underline;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const ProspectInfoValueButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$valueColor',
})<{ $valueColor: string }>`
  justify-content: flex-start;
  padding: 0;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: ${({ $valueColor }) => $valueColor};

  > span {
    margin: 0 !important;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const ProspectInfoPlainValue = styled(Typography)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const CopyFieldButton = styled(Button)`
  padding: 0;
  flex-shrink: 0;

  > svg {
    width: 16px;
    height: 16px;
    fill: ${tokens.BRAND_HERO_BLUE};
  }
`;

export { PipelineCardInfoItem };
export type { PipelineCardInfoItemProps };
